import { DvGridComponent } from '@devinco/ui/grid';
import {
  AfterViewInit,
  DestroyRef,
  Directive,
  inject,
  input,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestApiService } from '@core/services/rest-api.service';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { Entity } from '@shared/constants/entity';
import { AgGridPreferenceStore } from '@shared/directives/preferences/ag-grid-preference-store';
import { AgGridAngular } from 'ag-grid-angular';
import { ColumnState } from 'ag-grid-community';
import { debounceTime, filter, merge, Subject } from 'rxjs';

@Directive({
  selector: 'ag-grid-angular',
  standalone: false,
})
export class AgGridPreferenceDirective implements AfterViewInit, OnDestroy {
  private readonly agGrid = inject(AgGridAngular);
  private readonly restApi = inject(RestApiService);
  private readonly store = inject(AgGridPreferenceStore);
  private dvGrid = inject(DvGridComponent, { host: true });

  isSortSaveEnabled = input<boolean>(true);
  customColumnState = input<object>();
  customGridId = input<string>();

  private gridId: string;
  private isNew = true;
  private savedPreferences: ColumnState[] | null;
  private preferences: ColumnState[] | null;
  private entityId: number | null;
  private preferenceSaveTrigger$ = new Subject<boolean>();
  private wereSettingsSaved: boolean;
  private destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.gridId = this.customGridId() ?? this.dvGrid.context.entity!;

      if (
        !this.gridId ||
        !this.dvGrid
          .actions()
          ?.find(action => action.icon === vtPresetActions.reset.icon)
      ) {
        // grids without preferences still need the initial server side load
        this.triggerServerSideGridLoad();
        return;
      }

      this.getCurrentSettings();
      this.listenToChanges();
    });
  }

  ngOnDestroy(): void {
    this.savePreferences();
  }

  private getCurrentSettings(): void {
    this.savedPreferences = this.store.getPreference(this.gridId);
    this.entityId = this.store.getGridId(this.gridId);

    if (!this.savedPreferences) {
      this.restApi
        .getCustom(`UserSetting/Id/${this.gridId}`)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .subscribe((preferences: any) => {
          if (preferences) {
            this.preferences = this.savedPreferences = JSON.parse(
              preferences.Value
            );
            this.entityId = preferences.UserSettingId;
            this.store.setPreference(this.gridId, this.preferences!);
            this.store.setGridId(this.gridId, this.entityId!);
            this.applyExistingPreferences();
          } else {
            this.triggerServerSideGridLoad();
          }
        });
    } else {
      this.applyExistingPreferences();
    }
    this.listenToPreferenceSaveTrigger();
  }

  private listenToChanges(): void {
    merge(
      this.agGrid.columnResized,
      this.agGrid.columnPinned,
      this.agGrid.columnMoved,
      this.agGrid.columnVisible,
      this.agGrid.sortChanged,
      this.agGrid.columnRowGroupChanged
    )
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(_ => {
        this.wereSettingsSaved = false;
        this.preferences = this.agGrid.api.getColumnState();

        if (!this.isSortSaveEnabled()) {
          this.preferences.forEach(q => (q.sort = null));
        }

        this.preferenceSaveTrigger$.next(true);
      });
  }

  private applyExistingPreferences(): void {
    this.isNew = false;
    this.agGrid.api.applyColumnState({
      state: this.savedPreferences as ColumnState[],
      applyOrder: true,
    });

    const customColumnState = this.customColumnState();
    if (customColumnState) {
      this.agGrid.api.applyColumnState(customColumnState);
    }

    this.triggerServerSideGridLoad();
  }

  private triggerServerSideGridLoad(): void {
    if (this.agGrid.gridOptions?.rowModelType !== 'clientSide') {
      // do not trigger getRows until sortModel is applied
      setTimeout(() => {
        (this.dvGrid.context as GridContext).isGridPreloadCompleted = true;
        this.agGrid.api.onFilterChanged();
        this.dvGrid.agGrid.api.onFilterChanged();
      });
    }
  }

  private savePreferences(): void {
    if (
      this.gridId &&
      this.preferences &&
      JSON.stringify(this.preferences) !==
        JSON.stringify(this.savedPreferences) &&
      !this.wereSettingsSaved
    ) {
      this.wereSettingsSaved = true;

      this.restApi
        .upsert(Entity.Preference, this.isNew ? 0 : (this.entityId ?? 0), {
          Value: JSON.stringify(this.preferences),
          Id: this.gridId,
        })
        .subscribe();

      this.store.setPreference(this.gridId, this.preferences);
    }
  }

  private listenToPreferenceSaveTrigger(): void {
    this.preferenceSaveTrigger$
      .pipe(
        filter(changed => changed),
        debounceTime(10000),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.savePreferences();
      });
  }
}
